import { HttpError } from 'common-utils';
import {
  createPreflightCoreApiClient,
  DocumentId,
  isGeneric413,
  UploadErrorPayload,
} from 'preflight-core';
import { z } from 'zod';

import { preflightApiClient } from '../api/client';
import { isCochranePublisher } from '../utils/publisher-features';

type UploadFileType = {
  bigIntVisitorId?: string;
  preflightNudgeAccepted?: boolean;
  publisherId: string;
  journalId: string;
  file: File;
  parentDocumentId?: DocumentId;
  reUpload?: boolean;
  editorialDesk?: boolean;
  utm_medium?: string | string[] | null;
  utm_source?: string | string[] | null;
  utm_campaign?: string | string[] | null;
  referrer?: string | string[] | null;
  articleTypeId?: number;
  articleTypeStageId?: number;
  availableModules?: string[];
  manuscriptId?: string;
};

export function uploadFile({
  bigIntVisitorId,
  publisherId,
  preflightNudgeAccepted,
  journalId,
  file,
  parentDocumentId,
  reUpload,
  editorialDesk,
  utm_medium,
  utm_source,
  utm_campaign,
  referrer,
  articleTypeId,
  articleTypeStageId,
  availableModules,
  manuscriptId,
}: UploadFileType): Promise<UploadPayload> {
  console.info(
    '[uploadFile] Requesting documentId for fileName:',
    file.name,
    '| journalId:',
    journalId,
    '| publisherId:',
    publisherId,
    ...(manuscriptId ? ['| manuscriptId:', manuscriptId] : []),
  );
  return createPreflightCoreApiClient(preflightApiClient)
    .createDocumentId({
      journalId,
      publisherId,
      editorialDesk,
    })
    .then(({ documentId }) => {
      console.info(
        '[uploadFile] fileName:',
        file.name,
        '| documentId:',
        documentId,
        '| journalId:',
        journalId,
        '| publisherId:',
        publisherId,
        ...(manuscriptId ? ['| manuscriptId:', manuscriptId] : []),
      );
      return uploadWithDocumentId({
        bigIntVisitorId,
        documentId,
        file,
        journalId,
        publisherId,
        parentDocumentId,
        reUpload,
        editorialDesk,
        utm_medium,
        preflightNudgeAccepted,
        utm_source,
        utm_campaign,
        referrer,
        articleTypeId,
        articleTypeStageId,
        availableModules,
        manuscriptId,
      });
    });
}

function uploadWithDocumentId({
  bigIntVisitorId,
  documentId,
  file,
  journalId,
  publisherId,
  parentDocumentId,
  reUpload,
  editorialDesk,
  utm_medium,
  preflightNudgeAccepted,
  utm_source,
  utm_campaign,
  referrer,
  articleTypeId,
  articleTypeStageId,
  availableModules,
  manuscriptId,
}: {
  bigIntVisitorId?: string;
  documentId: DocumentId;
  file: File;
  journalId: string;
  publisherId: string;
  preflightNudgeAccepted?: boolean;
  parentDocumentId?: DocumentId;
  reUpload?: boolean;
  editorialDesk?: boolean;
  utm_medium?: string | string[] | null;
  utm_source?: string | string[] | null;
  utm_campaign?: string | string[] | null;
  referrer?: string | string[] | null;
  articleTypeId?: number;
  articleTypeStageId?: number;
  availableModules?: string[];
  manuscriptId?: string;
}): Promise<UploadPayload> {
  const isCochrane = isCochranePublisher(publisherId, journalId);
  const isSpringerNature =
    publisherId === 'springer-nature' || publisherId === 'test-hd';
  const maxFileSize =
    isSpringerNature || isCochrane ? 90 * 1024 * 1024 : 20 * 1024 * 1024;

  if (file.size >= maxFileSize) {
    const newPayload: UploadErrorPayload = {
      errorCode: 'UPLOAD_TOO_LARGE',
      errorReason: 'Document is too large for processing.',
    };

    return Promise.reject({ json: newPayload });
  }

  const formData = new FormData();
  const isV2Upload = availableModules && availableModules.length > 0;

  formData.append('file', file);

  if (!isV2Upload) {
    if (referrer) {
      formData.append('referrer', `${referrer}`);
    }
    if (bigIntVisitorId) {
      formData.append('bigIntVisitorId', bigIntVisitorId);
    }
    if (preflightNudgeAccepted) {
      formData.append('preflightNudgeAccepted', 'true');
    }
    formData.append('documentId', documentId);
    if (utm_medium !== null) {
      formData.append('utmMedium', `${utm_medium}`);
    }
    if (utm_source !== null) {
      formData.append('utmSource', `${utm_source}`);
    }
    if (utm_campaign !== null) {
      formData.append('utmCampaign', `${utm_campaign}`);
    }
  }

  if (reUpload && parentDocumentId) {
    formData.append('parentDocumentId', `${parentDocumentId}`);
    formData.append('reUpload', 'true');
  }
  if (
    !isV2Upload &&
    articleTypeId !== undefined &&
    publisherId !== 'endnote' &&
    publisherId !== 'refworks' &&
    publisherId !== 'endnote-fusion'
  ) {
    formData.append('articleTypeId', `${articleTypeId}`);
  }

  if (manuscriptId && manuscriptId.trim()) {
    formData.append('manuscriptId', manuscriptId.trim());
  }

  const ppePrefix = editorialDesk ? '/ppe' : '';

  let apiEndpoint = reUpload
    ? `${ppePrefix}/publisher/${publisherId}/journal/${journalId}/re-upload`
    : `${ppePrefix}/publisher/${publisherId}/journal/${journalId}/upload`;

  if (isV2Upload) {
    const queryParams = new URLSearchParams();
    queryParams.set('documentId', documentId);

    availableModules.forEach((module) => {
      queryParams.append('modules', module.toLowerCase());
    });

    if (articleTypeId !== undefined) {
      queryParams.set('articleTypeId', `${articleTypeId}`);
    }
    if (articleTypeStageId !== undefined) {
      queryParams.set('articleTypeStageId', `${articleTypeStageId}`);
    }
    if (bigIntVisitorId) {
      queryParams.set('bigIntVisitorId', bigIntVisitorId);
    }
    if (referrer) {
      queryParams.set('referrer', `${referrer}`);
    }
    if (utm_medium !== null && utm_medium !== undefined) {
      queryParams.set('utmMedium', `${utm_medium}`);
    }
    if (utm_source !== null && utm_source !== undefined) {
      queryParams.set('utmSource', `${utm_source}`);
    }
    if (utm_campaign !== null && utm_campaign !== undefined) {
      queryParams.set('utmCampaign', `${utm_campaign}`);
    }
    if (preflightNudgeAccepted) {
      queryParams.set('preflightNudgeAccepted', 'true');
    }
    apiEndpoint = `${ppePrefix}/v2/publisher/${publisherId}/journal/${journalId}/upload?${queryParams.toString()}`;
  }

  return preflightApiClient
    .post(apiEndpoint, formData)
    .then(UploadPayload.parse)
    .catch((error: HttpError) => {
      if (isGeneric413(error)) {
        const newPayload: UploadErrorPayload = {
          errorCode: 'UPLOAD_TOO_LARGE',
          errorReason: 'Document is too large for processing.',
        };
        return Promise.reject(error.withPayload(newPayload));
      }

      return Promise.reject(error);
    });
}

const UploadPayload = z.object({
  documentId: DocumentId,
  hasExistingTrackChanges: z.boolean().optional(),
  hasMacros: z.boolean().optional(),
  isCreatedFromDocx: z.boolean().optional(),
  existingManuscriptId: z.boolean().optional(),
});

export type UploadPayload = z.infer<typeof UploadPayload>;

export const UploadPayloadSearchParams = z.object({
  hasExistingTrackChanges: z
    .literal('true')
    .optional()
    .transform((value) => value === 'true'),
  hasMacros: z
    .literal('true')
    .optional()
    .transform((value) => value === 'true'),
});
